import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatDate, formatCOP, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { useConfirm } from "../components/useConfirm";
import { toast } from "sonner";

const ROLE_OPTS = [["all", "Todos"], ["user", "Usuario"], ["reviewer", "Revisor"], ["admin", "Admin"]];
const STATUS_OPTS = [["all", "Todos"], ["active", "Activos"], ["disabled", "Deshabilitados"]];

export default function AdminUsersPage() {
  const [filters, setFilters] = useState({ role: "all", status_: "all", q: "" });
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [roleModal, setRoleModal] = useState({ open: false, user: null, new_role: "user" });
  const [pwModal, setPwModal] = useState({ open: false, user: null, password: "" });
  // Fase 12 Sprint 2: modal de cuota + drawer de actividad
  const [quotaModal, setQuotaModal] = useState({ open: false, user: null, prev_limit: 3, diag_limit: 0, kit_limit: 0, year_month: "" });
  const [savingQuota, setSavingQuota] = useState(false);
  const [activityModal, setActivityModal] = useState({ open: false, user: null, data: null, loading: false });

  // Iter24.d: confirm dialog reutilizable
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (filters.role && filters.role !== "all") params.set("role", filters.role);
      if (filters.status_ && filters.status_ !== "all") params.set("status_", filters.status_);
      if (filters.q.trim()) params.set("q", filters.q.trim());
      const { data: d } = await api.get(`/admin/users?${params}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "No se pudieron cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const submitRoleChange = async () => {
    try {
      await api.post(`/admin/users/${roleModal.user.id}/change-role`, { new_role: roleModal.new_role });
      toast.success("Rol actualizado");
      setRoleModal({ open: false, user: null, new_role: "user" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    }
  };

  const toggleDisable = async (u) => {
    const verb = u.is_disabled ? "enable" : "disable";
    if (!u.is_disabled) {
      const ok = await confirm({
        title: "Deshabilitar cuenta",
        description: <>¿Deshabilitar a <strong>{u.email}</strong>? No podrá iniciar sesión hasta que la habilites de nuevo.</>,
        confirmLabel: "Deshabilitar",
        destructive: true,
        testIdPrefix: "disable-user-confirm",
      });
      if (!ok) return;
    }
    try {
      await api.post(`/admin/users/${u.id}/${verb}`);
      toast.success(u.is_disabled ? "Cuenta habilitada" : "Cuenta deshabilitada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    }
  };

  const resetPw = async (u) => {
    const ok = await confirm({
      title: "Generar nuevo password",
      description: <>¿Generar password nuevo para <strong>{u.email}</strong>? El password actual quedará invalidado inmediatamente.</>,
      confirmLabel: "Generar password",
      testIdPrefix: "reset-pw-confirm",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setPwModal({ open: true, user: u, password: data.new_password });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    }
  };

  const copyPw = () => {
    navigator.clipboard?.writeText(pwModal.password);
    toast.success("Password copiado al portapapeles");
  };

  // Fase 12 Sprint 2: editar cuota (3 contadores)
  const openQuotaModal = (u) => {
    const ym = u.quota_month || (() => {
      const d = new Date();
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    })();
    setQuotaModal({
      open: true,
      user: u,
      prev_limit: u.quota_prevalidations_limit ?? 3,
      diag_limit: u.quota_diagnoses_limit ?? 0,
      kit_limit: u.quota_kits_limit ?? 0,
      year_month: ym,
    });
  };

  const submitQuota = async () => {
    const prev = parseInt(quotaModal.prev_limit, 10);
    const diag = parseInt(quotaModal.diag_limit, 10);
    const kit = parseInt(quotaModal.kit_limit, 10);
    if (Number.isNaN(prev) || prev < 0 || prev > 1000) {
      toast.error("Cuota de prevalidaciones inválida (0-1000)");
      return;
    }
    if (Number.isNaN(diag) || diag < 0 || diag > 1000) {
      toast.error("Cuota de diagnósticos inválida (0-1000)");
      return;
    }
    if (Number.isNaN(kit) || kit < 0 || kit > 1000) {
      toast.error("Cuota de kits inválida (0-1000)");
      return;
    }
    setSavingQuota(true);
    try {
      await api.post(`/admin/users/${quotaModal.user.id}/quota`, {
        year_month: quotaModal.year_month,
        prevalidations_limit: prev,
        diagnoses_limit: diag,
        kits_limit: kit,
      });
      toast.success(`Cuotas actualizadas para ${quotaModal.year_month}`);
      setQuotaModal({ open: false, user: null, prev_limit: 3, diag_limit: 0, kit_limit: 0, year_month: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando cuotas");
    } finally {
      setSavingQuota(false);
    }
  };

  // Fase 12 Sprint 2: ver actividad del usuario
  const openActivityModal = async (u) => {
    setActivityModal({ open: true, user: u, data: null, loading: true });
    try {
      const { data: act } = await api.get(`/admin/users/${u.id}/activity`);
      setActivityModal({ open: true, user: u, data: act, loading: false });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando actividad");
      setActivityModal({ open: false, user: null, data: null, loading: false });
    }
  };

  return (
    <div className="container-prose py-8 max-w-6xl" data-testid="admin-users-page">
      <Link to="/admin" className="text-sm text-gray-500 hover:text-primary">← Admin</Link>
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mt-4 mb-1">Track B.3</p>
      <h1 className="font-serif text-3xl text-gray-900 mb-6">Gestión de usuarios</h1>

      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Rol</label>
          <Select value={filters.role} onValueChange={(v) => setFilters((f) => ({ ...f, role: v }))}>
            <SelectTrigger className="w-36 rounded-sm h-9" data-testid="users-filter-role"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>{ROLE_OPTS.map(([v, l]) => <SelectItem key={v || "all"} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Estado</label>
          <Select value={filters.status_} onValueChange={(v) => setFilters((f) => ({ ...f, status_: v }))}>
            <SelectTrigger className="w-44 rounded-sm h-9" data-testid="users-filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>{STATUS_OPTS.map(([v, l]) => <SelectItem key={v || "all"} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Buscar</label>
          <Input placeholder="email o nombre" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className="rounded-sm h-9" data-testid="users-filter-q" />
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-2">{data.total} usuario(s) — mostrando {data.items.length}</div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
            <th className="px-4 py-3 text-left font-medium">Email</th>
            <th className="px-3 py-3 text-left font-medium">Nombre</th>
            <th className="px-3 py-3 text-left font-medium">Rol</th>
            <th className="px-3 py-3 text-left font-medium">Estado</th>
            <th className="px-3 py-3 text-right font-medium">Casos</th>
            <th className="px-3 py-3 text-right font-medium">Cuota (mes)</th>
            <th className="px-3 py-3 text-right font-medium">Último login</th>
            <th className="px-4 py-3 text-right font-medium">Acciones</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-500" data-testid="users-empty">Sin resultados.</td></tr>}
            {!loading && data.items.map((u) => (
              <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50 align-middle" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 text-gray-900 max-w-[260px] truncate">{u.email}</td>
                <td className="px-3 py-3 text-gray-700">{u.name || "—"}</td>
                <td className="px-3 py-3"><span className="inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider bg-gray-100 text-gray-800 border-gray-300">{u.role}</span></td>
                <td className="px-3 py-3">
                  {u.is_disabled
                    ? <span className="inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider bg-rose-50 text-rose-900 border-rose-300">Deshabilitado</span>
                    : <span className="inline-flex border px-2 py-[3px] text-[11px] uppercase tracking-wider bg-emerald-50 text-emerald-900 border-emerald-300">Activo</span>}
                </td>
                <td className="px-3 py-3 text-right text-gray-700">{u.cases_count}</td>
                <td className="px-3 py-3 text-right text-xs whitespace-nowrap" data-testid={`user-quota-${u.id}`}>
                  {u.role === "user" ? (
                    <div className="space-y-0.5">
                      <div>
                        <span className="text-[9px] text-gray-500 uppercase tracking-widest">P</span>{" "}
                        <span className={`font-mono ${u.quota_prevalidations_used >= u.quota_prevalidations_limit ? "text-rose-700" : "text-gray-700"}`}>
                          {u.quota_prevalidations_used ?? 0}/{u.quota_prevalidations_limit ?? 3}
                        </span>
                      </div>
                      <div>
                        <span className="text-[9px] text-gray-500 uppercase tracking-widest">D</span>{" "}
                        <span className={`font-mono ${(u.quota_diagnoses_limit ?? 0) > 0 ? "text-emerald-700" : "text-gray-500"}`}>
                          {u.quota_diagnoses_used ?? 0}/{u.quota_diagnoses_limit ?? 0}
                        </span>
                      </div>
                      <div>
                        <span className="text-[9px] text-gray-500 uppercase tracking-widest">K</span>{" "}
                        <span className={`font-mono ${(u.quota_kits_limit ?? 0) > 0 ? "text-emerald-700" : "text-gray-500"}`}>
                          {u.quota_kits_used ?? 0}/{u.quota_kits_limit ?? 0}
                        </span>
                      </div>
                      {u.quota_set_by_admin && (
                        <div className="text-[8px] uppercase bg-amber-50 text-amber-800 border border-amber-300 px-1 inline-block mt-0.5">
                          override
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-right text-xs text-gray-500 whitespace-nowrap">{u.last_login_at ? formatDate(u.last_login_at) : "Nunca"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <div className="inline-flex gap-1">
                    {u.role === "user" && (
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] border-gray-300" onClick={() => openQuotaModal(u)} data-testid={`quota-${u.id}`}>Cuota</Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] border-gray-300" onClick={() => openActivityModal(u)} data-testid={`activity-${u.id}`}>Actividad</Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] border-gray-300" onClick={() => setRoleModal({ open: true, user: u, new_role: u.role })} data-testid={`role-${u.id}`}>Rol</Button>
                    <Button size="sm" variant="outline" className={`rounded-sm h-7 text-[11px] ${u.is_disabled ? "border-emerald-300 text-emerald-700" : "border-rose-300 text-rose-700"}`} onClick={() => toggleDisable(u)} data-testid={`toggle-${u.id}`}>
                      {u.is_disabled ? "Habilitar" : "Deshabilitar"}
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] border-gray-300" onClick={() => resetPw(u)} data-testid={`reset-${u.id}`}>Reset pass</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Role modal */}
      <Dialog open={roleModal.open} onOpenChange={(o) => !o && setRoleModal({ open: false, user: null, new_role: "user" })}>
        <DialogContent className="rounded-sm" data-testid="role-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Cambiar rol</DialogTitle>
            <DialogDescription>{roleModal.user?.email}</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={roleModal.new_role} onValueChange={(v) => setRoleModal((m) => ({ ...m, new_role: v }))}>
              <SelectTrigger className="rounded-sm" data-testid="role-modal-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="user">Usuario</SelectItem>
                <SelectItem value="reviewer">Revisor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-gray-300" onClick={() => setRoleModal({ open: false, user: null, new_role: "user" })}>Cancelar</Button>
            <Button className="rounded-sm" onClick={submitRoleChange} data-testid="role-modal-submit">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password modal */}
      <Dialog open={pwModal.open} onOpenChange={(o) => !o && setPwModal({ open: false, user: null, password: "" })}>
        <DialogContent className="rounded-sm" data-testid="pw-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Password generado</DialogTitle>
            <DialogDescription>
              Cópialo ahora. Por seguridad <strong>no se mostrará otra vez</strong>. Envíaselo al usuario por canal seguro.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <p className="text-xs text-gray-500 mb-1">Para: <strong>{pwModal.user?.email}</strong></p>
            <div className="font-mono text-lg bg-gray-50 border border-gray-300 px-4 py-3 select-all" data-testid="pw-modal-value">{pwModal.password}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-gray-300" onClick={copyPw} data-testid="pw-modal-copy">Copiar</Button>
            <Button className="rounded-sm" onClick={() => setPwModal({ open: false, user: null, password: "" })} data-testid="pw-modal-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Fase 12 Sprint 2: Quota modal (3 contadores) */}
      <Dialog open={quotaModal.open} onOpenChange={(o) => !o && setQuotaModal({ open: false, user: null, prev_limit: 3, diag_limit: 0, kit_limit: 0, year_month: "" })}>
        <DialogContent className="rounded-sm" data-testid="quota-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Cuotas mensuales gratuitas</DialogTitle>
            <DialogDescription>
              {quotaModal.user?.email} — mes {quotaModal.year_month}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div className="border border-gray-200 p-2 text-center">
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Prevalid.</p>
                <p className="font-mono text-sm">
                  {quotaModal.user?.quota_prevalidations_used ?? 0}/{quotaModal.user?.quota_prevalidations_limit ?? 3}
                </p>
              </div>
              <div className="border border-gray-200 p-2 text-center">
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Diagnósticos</p>
                <p className="font-mono text-sm">
                  {quotaModal.user?.quota_diagnoses_used ?? 0}/{quotaModal.user?.quota_diagnoses_limit ?? 0}
                </p>
              </div>
              <div className="border border-gray-200 p-2 text-center">
                <p className="text-[10px] uppercase tracking-widest text-gray-500">Kits</p>
                <p className="font-mono text-sm">
                  {quotaModal.user?.quota_kits_used ?? 0}/{quotaModal.user?.quota_kits_limit ?? 0}
                </p>
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">Prevalidaciones (límite)</label>
              <Input
                type="number"
                min="0"
                max="1000"
                value={quotaModal.prev_limit}
                onChange={(e) => setQuotaModal((m) => ({ ...m, prev_limit: e.target.value }))}
                className="rounded-sm h-9"
                data-testid="quota-modal-prev-limit"
              />
              <p className="text-[10px] text-gray-500 mt-1">Default: 3 al mes (gratuito para todos los usuarios).</p>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">Diagnósticos (límite gratuito)</label>
              <Input
                type="number"
                min="0"
                max="1000"
                value={quotaModal.diag_limit}
                onChange={(e) => setQuotaModal((m) => ({ ...m, diag_limit: e.target.value }))}
                className="rounded-sm h-9"
                data-testid="quota-modal-diag-limit"
              />
              <p className="text-[10px] text-gray-500 mt-1">Default: 0 (los usuarios pagan). Asigna &gt;0 como obsequio/demo.</p>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">Kits Documentales (límite gratuito)</label>
              <Input
                type="number"
                min="0"
                max="1000"
                value={quotaModal.kit_limit}
                onChange={(e) => setQuotaModal((m) => ({ ...m, kit_limit: e.target.value }))}
                className="rounded-sm h-9"
                data-testid="quota-modal-kit-limit"
              />
              <p className="text-[10px] text-gray-500 mt-1">Default: 0. Asigna &gt;0 como obsequio.</p>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">Mes (YYYY-MM)</label>
              <Input
                value={quotaModal.year_month}
                onChange={(e) => setQuotaModal((m) => ({ ...m, year_month: e.target.value }))}
                placeholder="2026-05"
                className="rounded-sm h-9 font-mono"
                data-testid="quota-modal-month"
              />
              <p className="text-[10px] text-gray-500 mt-1">Aplica solo para el mes indicado.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm border-gray-300" onClick={() => setQuotaModal({ open: false, user: null, prev_limit: 3, diag_limit: 0, kit_limit: 0, year_month: "" })}>Cancelar</Button>
            <Button className="rounded-sm" onClick={submitQuota} disabled={savingQuota} data-testid="quota-modal-submit">
              {savingQuota ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase 12 Sprint 2: Activity modal */}
      <Dialog open={activityModal.open} onOpenChange={(o) => !o && setActivityModal({ open: false, user: null, data: null, loading: false })}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="activity-modal">
          <DialogHeader>
            <DialogTitle className="font-serif">Actividad del usuario</DialogTitle>
            <DialogDescription>{activityModal.user?.email}</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4 text-sm">
            {activityModal.loading && <p className="text-gray-500">Cargando actividad…</p>}
            {!activityModal.loading && activityModal.data && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <KPI label="Casos" value={activityModal.data.cases_count} />
                  <KPI label="Prevalid." value={activityModal.data.prevalidations?.total ?? 0} />
                  <KPI label="Diagnósticos" value={activityModal.data.diagnoses?.total ?? 0} />
                  <KPI label="Kits" value={activityModal.data.kits?.total ?? 0} />
                </div>

                <div className="border border-gray-200 p-3">
                  <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Pagos confirmados</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>Reales: <strong>{activityModal.data.payments_summary?.confirmed_count ?? 0}</strong> · {formatCOP(activityModal.data.payments_summary?.confirmed_amount_cop ?? 0)}</div>
                    <div>Obsequios: <strong>{activityModal.data.payments_summary?.complimentary_count ?? 0}</strong> · {formatCOP(activityModal.data.payments_summary?.complimentary_amount_cop ?? 0)}</div>
                  </div>
                </div>

                {(activityModal.data.quotas_last_12_months || []).length > 0 && (
                  <div className="border border-gray-200 p-3">
                    <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Cuotas (últimos meses)</p>
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-gray-200"><th className="text-left py-1">Mes</th><th className="text-right py-1">Usadas</th><th className="text-right py-1">Límite</th><th className="text-right py-1">Override</th></tr></thead>
                      <tbody>
                        {activityModal.data.quotas_last_12_months.map((q) => (
                          <tr key={q.year_month} className="border-b border-gray-100">
                            <td className="py-1 font-mono">{q.year_month}</td>
                            <td className="py-1 text-right">{q.prevalidations_used}</td>
                            <td className="py-1 text-right">{q.prevalidations_limit}</td>
                            <td className="py-1 text-right">{q.set_by_admin ? "Sí" : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(activityModal.data.diagnoses?.items || []).length > 0 && (
                  <div className="border border-gray-200 p-3">
                    <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Diagnósticos generados (versiones)</p>
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-gray-200"><th className="text-left py-1">Case</th><th className="text-right py-1">V</th><th className="text-left py-1">Estado</th><th className="text-left py-1">Variante</th><th className="text-right py-1">Fecha</th></tr></thead>
                      <tbody>
                        {activityModal.data.diagnoses.items.slice(0, 20).map((d) => (
                          <tr key={d.id} className="border-b border-gray-100">
                            <td className="py-1 font-mono text-[10px]">{d.case_id?.slice(0, 8)}…</td>
                            <td className="py-1 text-right">{d.version}</td>
                            <td className="py-1">{d.effective_status || d.human_review_status || "—"}</td>
                            <td className="py-1">{d.auto_variant ? "auto" : "reviewed"}</td>
                            <td className="py-1 text-right text-gray-500">{d.created_at ? formatDate(d.created_at) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(activityModal.data.kits?.items || []).length > 0 && (
                  <div className="border border-gray-200 p-3">
                    <p className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Kits N2 generados (versiones)</p>
                    <table className="w-full text-xs">
                      <thead><tr className="border-b border-gray-200"><th className="text-left py-1">Case</th><th className="text-right py-1">V</th><th className="text-left py-1">Estado</th><th className="text-left py-1">Variante</th><th className="text-right py-1">Fecha</th></tr></thead>
                      <tbody>
                        {activityModal.data.kits.items.slice(0, 20).map((k) => (
                          <tr key={k.id} className="border-b border-gray-100">
                            <td className="py-1 font-mono text-[10px]">{k.case_id?.slice(0, 8)}…</td>
                            <td className="py-1 text-right">{k.version}</td>
                            <td className="py-1">{k.human_review_status || "—"}</td>
                            <td className="py-1">{k.auto_variant ? "auto" : "reviewed"}</td>
                            <td className="py-1 text-right text-gray-500">{k.created_at ? formatDate(k.created_at) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button className="rounded-sm" onClick={() => setActivityModal({ open: false, user: null, data: null, loading: false })}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter24.d — Confirm dialog reutilizable */}
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="border border-gray-200 p-2 text-center">
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className="font-serif text-xl text-gray-900">{value}</p>
    </div>
  );
}
